import createDOMPurify from 'dompurify'
import { htmlSanitizationPolicies, type HtmlSanitizationPolicy } from '~/utils/html-sanitization'

export type SanitizeHtml = (html: string | undefined | null, policy?: HtmlSanitizationPolicy) => string

export default defineNuxtPlugin({
  name: 'sanitize-html',
  async setup() {
    let sanitizeHtml: (html: string, policy: HtmlSanitizationPolicy) => string

    if (import.meta.server) {
      const { sanitizeHtmlServer } = await import('~~/server/utils/html-sanitizer')
      sanitizeHtml = (html, policy) => sanitizeHtmlServer(html, policy)
    }
    else {
      // Client: use DOMPurify
      const purify = createDOMPurify(window)
      sanitizeHtml = (html, policy) => {
        const config = htmlSanitizationPolicies[policy]
        const sanitizedHtml = purify.sanitize(html, {
          ALLOWED_TAGS: config.ALLOWED_TAGS as string[],
          ALLOWED_ATTR: config.ALLOWED_ATTR as string[],
          ALLOW_DATA_ATTR: false,
        })

        if (!config.ALLOWED_STYLES) {
          return sanitizedHtml
        }

        const document = new DOMParser().parseFromString(sanitizedHtml, 'text/html')
        document.body.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
          const allowedProperties = config.ALLOWED_STYLES?.[element.tagName.toLowerCase()]
          for (const property of Array.from(element.style)) {
            const value = element.style.getPropertyValue(property).trim()
            if (!allowedProperties?.[property]?.some(pattern => pattern.test(value))) {
              element.style.removeProperty(property)
            }
          }

          if (!element.style.cssText) {
            element.removeAttribute('style')
          }
        })

        return document.body.innerHTML
      }
    }

    return {
      provide: {
        sanitizeHtml(html: string | undefined | null, policy: HtmlSanitizationPolicy = 'richText'): ReturnType<SanitizeHtml> {
          if (!html) {
            return ''
          }

          return sanitizeHtml(html, policy)
        },
      },
    }
  },
})
